import { useEffect, useRef } from "react";
import { useNavigateCarrying } from "@/lib/carryParams";
import { tr } from "@/lib/i18n";
import { pushToast } from "@/hooks/useToasts";
import { useAlarms } from "@/hooks/useAlarms";
import { useTopology } from "@/store/site";
import { setAlarmBadge } from "@/lib/docTitle";

/* ------------------------------------------------------------------------ */
/* Favicon badge (XTRA-02)                                                   */
/* ------------------------------------------------------------------------ */

/**
 * Best-effort red-dot favicon while critical/major alarms are active, so a
 * backgrounded operator gets a signal even when the tab title is truncated
 * to the icon. The original icon (index.html's inline SVG data: URL) is
 * redrawn onto a canvas with a dot overlay; everything is wrapped in
 * try/catch because none of it is essential — sandboxed iframes (the TB
 * widget embed), CSP-restricted canvases or a missing <link rel="icon">
 * simply leave the icon untouched.
 */
let faviconOriginal: { href: string; type: string } | null = null;
let faviconBadged = false;

function setFaviconBadge(on: boolean) {
  try {
    if (on === faviconBadged) return;
    faviconBadged = on;
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;
    if (!on) {
      // Count dropped to 0 → restore the pristine icon.
      if (faviconOriginal) {
        link.href = faviconOriginal.href;
        if (faviconOriginal.type) link.type = faviconOriginal.type;
        else link.removeAttribute("type");
      }
      return;
    }
    if (!faviconOriginal) faviconOriginal = { href: link.href, type: link.type };
    const img = new Image();
    img.onload = () => {
      try {
        // Async guard: if the alarms cleared while the image was decoding,
        // don't stamp a stale badge over the just-restored original.
        if (!faviconBadged) return;
        const size = 64;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, size, size);
        // Red dot top-right with a knockout ring so it stays legible at 16px.
        ctx.beginPath();
        ctx.arc(46, 18, 16, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(46, 18, 13, 0, Math.PI * 2);
        ctx.fillStyle = "#dc2626";
        ctx.fill();
        link.type = "image/png";
        link.href = canvas.toDataURL("image/png");
      } catch {
        /* best-effort — leave whatever icon is currently set */
      }
    };
    // data: URLs are same-origin, so the canvas never taints.
    img.src = faviconOriginal.href;
  } catch {
    /* best-effort — no DOM access / no link element / CSP */
  }
}

/**
 * Emits a toast when a NEW critical or major alarm appears (vs the previous
 * observed set). Invoked once at app-shell level so every page gets toast
 * notifications.
 *
 * It now reads the SHARED alarms cache ({@link useAlarms}) instead of running
 * its own `setInterval` + `adapter.alarms()` poll. That removes one of the
 * three overlapping alarm fetches that were stacking up on the main thread
 * and contributing to the Overview's periodic stutter — the diff logic is
 * unchanged, it just runs whenever the shared query delivers fresh data.
 */
export function useAlarmWatcher() {
  const navigate = useNavigateCarrying();
  const topology = useTopology();
  const { data: alarms } = useAlarms();
  const seen = useRef<Set<string>>(new Set());
  const initialised = useRef(false);

  // A site swap delivers the new site's alarms under a fresh query key —
  // re-snapshot instead of toasting every pre-existing alarm as "new"
  // (ALM-05). Declared before the diff effect so the reset wins the commit.
  useEffect(() => {
    seen.current = new Set();
    initialised.current = false;
  }, [topology.id]);

  // Background-tab signal (XTRA-02): tab-title "(2🔴)" prefix + favicon dot
  // while critical/major alarms are ACTIVE; both clear at 0. Runs on every
  // shared-cache delivery (which keeps refetching in hidden tabs — see
  // useAlarms). While `alarms` is undefined (site swap re-keys the query)
  // the last badge is deliberately kept — "unknown" must not flash as
  // "all clear".
  useEffect(() => {
    if (!alarms) return;
    const count = alarms.filter(
      (a) => a.status === "active" && (a.severity === "critical" || a.severity === "major"),
    ).length;
    setAlarmBadge(count);
    setFaviconBadge(count > 0);
  }, [alarms]);

  useEffect(() => {
    if (!alarms) return;
    const active = alarms.filter((a) => a.status === "active");
    if (!initialised.current) {
      // On first delivery, just snapshot — don't toast existing alarms.
      for (const a of active) seen.current.add(a.id);
      initialised.current = true;
      return;
    }
    for (const a of active) {
      if (seen.current.has(a.id)) continue;
      seen.current.add(a.id);
      if (a.severity === "critical" || a.severity === "major") {
        pushToast({
          level: a.severity === "critical" ? "critical" : "warn",
          title: tr(
            a.severity === "critical" ? "manage.watch.criticalAlarm" : "manage.watch.majorAlarm",
            { device: a.device_name },
          ),
          detail: a.message,
          // Criticals stay until dismissed or actioned (ALM-05); the
          // Toaster's X button is the escape hatch.
          duration: a.severity === "critical" ? Infinity : 8000,
          // Deep-link to THIS alarm: the console expands, scrolls to and
          // highlights the row (?id= contract). Carries window + session
          // params and replaces history when TB-embedded.
          action: { label: tr("manage.watch.openAlarm"), onAction: () => navigate("/alarms", { id: a.id }) },
        });
      }
    }
    // Drop seen IDs that are no longer active so a re-raise re-toasts.
    const activeIds = new Set(active.map((a) => a.id));
    for (const id of Array.from(seen.current)) {
      if (!activeIds.has(id)) seen.current.delete(id);
    }
  }, [alarms, navigate]);
}
